/* eslint-disable react/prop-types */
import React, { useMemo, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { FileDownload } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { MaterialReactTable } from "material-react-table";
import moment from "moment";

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const YEARS = Array.from({ length: 5 }, (_, i) => moment().year() - 2 + i);

function MissionTracker() {
    const navigate = useNavigate();
    const location = useLocation();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [districts, setDistricts] = useState([]);
    const [filters, setFilters] = useState({
        district: "All",
        status: "All",
        month: moment().format("MMMM"),
        year: moment().format("YYYY")
    });

    useEffect(() => {
        if (location.state) {
            const newState = { ...filters };

            if (location.state.status) {
                newState.status = location.state.status;
            }

            if (location.state.month && location.state.year) {
                // Map numeric month (1-12) to month name
                const monthName = MONTHS[location.state.month - 1];
                newState.month = monthName;
                newState.year = String(location.state.year);
            }

            setFilters(newState);

            // Clear state to avoid re-applying on every render or back navigation
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        const fetchDistricts = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/districts/all`, {
                    withCredentials: true
                });
                console.log("Districts", response)
                if (response.status === 200) {
                    console.log(response)
                    setDistricts(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching districts:", error);
            }
        };
        fetchDistricts();
    }, []);

    useEffect(() => {
        const fetchUsers = async () => {
            setLoading(true);
            try {
                const params = {};
                if (filters.district !== "All") params.districts = filters.district;
                if (filters.status !== "All") params.status = filters.status;
                params.month = `${filters.month} ${filters.year}`;

                const response = await axios.get(`${process.env.REACT_APP_API_URL}/user-mission/users`, {
                    params,
                    withCredentials: true
                });
                if (response.data.status === 200) {
                    const formattedData = response.data.data.map((user, index) => ({
                        sno: index + 1,
                        id: user.value,
                        user: user.title,
                        district: user.district || "N/A",
                        hasRecords: user.has_records === 1 || user.has_records === true,
                        memberSince: user.member_since,
                        totalSubmissions: user.total_submissions,
                        lastSubmission: user.last_submission,
                        currentMonthSubmissions: user.current_month_submissions
                    }));
                    setData(formattedData);
                }
            } catch (error) {
                console.error("Error fetching users for mission tracker:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
    }, [filters]);

    const handleExportToExcel = () => {
        if (!data || data.length === 0) return;

        const exportData = data.map((row) => ({
          "S.No": row.sno,
          "Fellow Name": row.user,
          District: row.district,
          "Submission Status": row.currentMonthSubmissions >= 1 ? "Submitted" : "Not Submitted",
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);
        XLSX.utils.book_append_sheet(wb, ws, "Mission Tracker");

        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `Mission_Tracker_${filters.month}_${filters.year}.xlsx`);
    };

    const handleFilterChange = (field) => (event) => {
        setFilters(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const columns = useMemo(
      () => [
        {
          accessorKey: "sno",
          header: "S.No",
          size: 100,
        },
        {
          accessorKey: "user",
          header: "Fellow Name",
          size: 200,
          Cell: ({ renderedCellValue }) => (
            <MDTypography variant="button" fontWeight="medium" color="text">
              {renderedCellValue}
            </MDTypography>
          ),
        },
        {
          accessorKey: "district",
          header: "District",
          size: 150,
          filterVariant: "select",
          filterSelectOptions: districts.map((d) => d.title),
          Cell: ({ renderedCellValue }) => (
            <MDTypography variant="caption" color="text" fontWeight="bold">
              {renderedCellValue}
            </MDTypography>
          ),
        },
        // {
        //     accessorKey: "memberSince",
        //     header: "Joined On",
        //     size: 150,
        //     Cell: ({ cell }) => (
        //         <MDTypography variant="caption" color="text">
        //             {cell.getValue() ? moment(cell.getValue()).format("DD MMM YYYY") : "N/A"}
        //         </MDTypography>
        //     ),
        // },
        {
          id: "currentMonthSubmissions",
          accessorFn: (row) => (row.currentMonthSubmissions >= 1 ? "Submitted" : "Pending"),
          header: "Month Progress",
          size: 200,
          filterVariant: "select",
          filterSelectOptions: ["Submitted", "Pending"],
          muiTableHeadCellProps: { align: "center" },
          muiTableBodyCellProps: { align: "center" },
          Cell: ({ cell }) => {
            const status = cell.getValue();
            const isSubmitted = status === "Submitted";
            const color = isSubmitted ? "success" : "error";
            return (
              <MDBox display="flex" flexDirection="column" gap={0.5} alignItems="center">
                <MDBox
                  sx={{
                    px: 2,
                    py: 0.5,
                    borderRadius: "12px",
                    bgcolor: `${color}.main`,
                    color: "#fff",
                    fontSize: "11px",
                    fontWeight: "bold",
                    minWidth: "100px",
                    textAlign: "center",
                  }}
                >
                  {status}
                </MDBox>
                <Box
                  sx={{
                    width: "80%",
                    height: 4,
                    bgcolor: "#eee",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: isSubmitted ? "100%" : "0%",
                      height: "100%",
                      bgcolor: `${color}.main`,
                    }}
                  />
                </Box>
              </MDBox>
            );
          },
        },
        // {
        //     accessorKey: "totalSubmissions",
        //     header: "Total",
        //     size: 150,
        //     muiTableHeadCellProps: { align: 'center' },
        //     muiTableBodyCellProps: { align: 'center' },
        //     Cell: ({ renderedCellValue }) => (
        //         <MDTypography variant="button" fontWeight="medium">
        //             {renderedCellValue}
        //         </MDTypography>
        //     ),
        // },
        // {
        //     accessorKey: "lastSubmission",
        //     header: "Last Activity",
        //     size: 180,
        //     Cell: ({ cell }) => (
        //         <MDBox>
        //             <MDTypography variant="caption" color="text" fontWeight="medium" display="block">
        //                 {cell.getValue() ? moment(cell.getValue()).format("DD MMM YYYY") : "Never"}
        //             </MDTypography>
        //             {cell.getValue() && (
        //                 <MDTypography variant="caption" color="text" sx={{ fontSize: "10px", opacity: 0.7 }}>
        //                     {moment(cell.getValue()).fromNow()}
        //                 </MDTypography>
        //             )}
        //         </MDBox>
        //     ),
        // },

        {
          accessorKey: "view",
          header: "View",
          size: 100,
          enableColumnFilter: false,
          enableSorting: false,
          muiTableHeadCellProps: {
            align: "center",
          },
          muiTableBodyCellProps: {
            align: "center",
          },
          Cell: ({ row }) => (
            <MDBox display="flex" alignItems="center" justifyContent="center">
              {row.original.hasRecords ? (
                <Tooltip title="View Detailed Tracker">
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    iconOnly
                    circular
                    onClick={() =>
                      navigate(`/mission/${row.original.id}`, {
                        state: {
                          userId: row.original.id,
                          user_name: row.original.user,
                          month: filters.month,
                          year: filters.year,
                        },
                      })
                    }
                    sx={{
                      borderColor: "info.main",
                      "&:hover": {
                        backgroundColor: "info.main",
                        color: "white",
                      },
                    }}
                  >
                    <Icon>visibility</Icon>
                  </MDButton>
                </Tooltip>
              ) : (
                <MDTypography variant="caption" color="text" opacity={0.5}>
                  No Data
                </MDTypography>
              )}
            </MDBox>
          ),
        },
      ],
      [navigate, districts, filters]
    );

    return (
      <DashboardLayout>
        <MDBox pt={6} pb={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={2}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDBox display="flex" alignItems="center" gap={2}>
                  {/* <MDButton
                                    variant="outlined"
                                    color="white"
                                    size="small"
                                    onClick={() => navigate(-1)}
                                    sx={{
                                        minWidth: "40px",
                                        height: "40px",
                                        borderRadius: "50%",
                                        padding: 0,
                                        borderColor: "rgba(255,255,255,0.3)",
                                        "&:hover": {
                                            backgroundColor: "rgba(255,255,255,0.1)",
                                            borderColor: "white",
                                        }
                                    }}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>arrow_back</Icon>
                                </MDButton> */}
                  <MDBox>
                    <MDTypography variant="h6" color="white">
                      Mission Tracker
                    </MDTypography>
                    <MDTypography variant="button" color="white" opacity={0.8} fontWeight="regular">
                      Monitor and track fellow progress across districts
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDButton
                  variant="gradient"
                  color="success"
                  startIcon={<FileDownload />}
                  onClick={handleExportToExcel}
                  disabled={loading || data.length === 0}
                >
                  Export Excel
                </MDButton>
              </MDBox>

              <MDBox pt={3} px={2} display="flex" flexWrap="wrap" gap={2}>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="month-filter-label">Month</InputLabel>
                  <Select
                    labelId="month-filter-label"
                    value={filters.month}
                    onChange={handleFilterChange("month")}
                    label="Month"
                    sx={{ height: "40px" }}
                  >
                    {MONTHS.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="year-filter-label">Year</InputLabel>
                  <Select
                    labelId="year-filter-label"
                    value={filters.year}
                    onChange={handleFilterChange("year")}
                    label="Year"
                    sx={{ height: "40px" }}
                  >
                    {YEARS.map((y) => (
                      <MenuItem key={y} value={String(y)}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="district-filter-label">District</InputLabel>
                  <Select
                    labelId="district-filter-label"
                    value={filters.district}
                    onChange={handleFilterChange("district")}
                    label="District"
                    sx={{ height: "40px" }}
                  >
                    <MenuItem value="All">All Districts</MenuItem>
                    {districts.map((d) => (
                      <MenuItem key={d.value} value={d.value}>
                        {d.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="status-filter-label">Overall Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={filters.status}
                    onChange={handleFilterChange("status")}
                    label="Overall Status"
                    sx={{ height: "40px" }}
                  >
                    <MenuItem value="All">All Fellows</MenuItem>
                    <MenuItem value="Submitted">Submitted </MenuItem>
                    <MenuItem value="Not Submitted">Not Submitted </MenuItem>
                  </Select>
                </FormControl>
              </MDBox>

              <MDBox pt={3} pb={3} px={2}>
                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="300px"
                  >
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <MaterialReactTable
                    columns={columns}
                    data={data}
                    enableColumnFilters={true}
                    enableGlobalFilter={true}
                    enablePagination={true}
                    enableDensityToggle={false}
                    enableFullScreenToggle={false}
                    layoutMode="grid"
                    initialState={{
                      showColumnFilters: true,
                      density: "comfortable",
                      pagination: { pageSize: 10, pageIndex: 0 },
                    }}
                    muiTopToolbarProps={{
                      sx: {
                        backgroundColor: "#fff",
                        boxShadow: "none",
                      },
                    }}
                    muiTableHeadProps={{
                      sx: {
                        padding: "0px", // won't apply directly to <thead>, but useful for general structure
                        backgroundColor: "#011F3A", // background on <thead> element itself
                      },
                    }}
                    enableStickyHeader
                    muiTableContainerProps={{
                      sx: {
                        maxHeight: "70vh",
                        overflowY: "auto",
                      },
                    }}
                    muiTableHeadCellProps={{
                      sx: {
                        backgroundColor: "#004030",
                        color: "#fff",
                        // fontSize: '16px',
                        fontWeight: "bold",
                        position: "sticky", // Make header sticky
                        top: 0, // Stick it at the top
                        zIndex: 1, // Ensure it stays
                        "& .MuiTableSortLabel-root": {
                          "& .MuiTableSortLabel-icon": {
                            color: "#fff !important",
                          },
                        },
                        "& .MuiInputBase-input": {
                          color: "#fff !important",
                          fontSize: "12px",
                          "::placeholder": {
                            color: "#e0e0e0", // Default (blurred) placeholder color
                            opacity: 1,
                          },
                        },
                        "& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder": {
                          color: "#ffffff", // Focused placeholder color
                        },
                        "& .MuiIconButton-root": {
                          color: "#fff",
                        },
                        "& .MuiInput-underline:before": {
                          borderBottomColor: "#e0e0e0",
                        },
                        "& .MuiInput-underline:after": {
                          borderBottomColor: "#fff",
                        },
                        "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                          borderBottomColor: "#fff",
                        },

                        "& .MuiCheckbox-root": {
                          color: "#fff",
                        },
                      },
                    }}
                    muiTableBodyCellProps={{
                      sx: {
                        padding: "8px", // reduce from default 16px
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                    muiTableBodyRowProps={({ row }) => ({
                      sx: {
                        backgroundColor: row.index % 2 === 0 ? "#fafafa" : "#f0f0f0", // soft alternating colors
                      },
                    })}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
}

export default MissionTracker;
